import { Paper, ScrollArea, Text, UnstyledButton } from "@mantine/core";
import { ReactRenderer } from "@tiptap/react";
import { exitSuggestion, type SuggestionKeyDownProps } from "@tiptap/suggestion";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
    type MouseEvent
} from "react";

import {
    mergeFieldSuggestionPluginKey,
    type MergeFieldOption,
    type MergeFieldOptions
} from "./MergeField";
import classes from "./MergeFieldSuggestionList.module.css";

export type MergeFieldSuggestionListProps = {
    items: readonly MergeFieldOption[];
    command: (field: MergeFieldOption) => void;
    focusOnMount?: boolean;
    onClose?: () => void;
    withSurface?: boolean;
};

export type MergeFieldSuggestionListRef = {
    onKeyDown: (props: SuggestionKeyDownProps) => boolean;
};

/**
 * Renders keyboard- and pointer-selectable merge-field options with optional surface/focus behavior.
 * Postconditions: New item arrays request index zero. Effects focus the selected item when requested
 * and scroll it into view; those changes occur after render. Choosing a field delegates to command.
 */
export const MergeFieldSuggestionList = forwardRef<
    MergeFieldSuggestionListRef,
    MergeFieldSuggestionListProps
>(({ items, command, focusOnMount = false, onClose, withSurface = false }, ref) => {
const [selectedIndex, setSelectedIndex] = useState(0);
const viewportRef = useRef<HTMLDivElement>(null);
const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

useEffect(
    () => {
        setSelectedIndex(0);
    },
    [items]
);

useEffect(
    () => {
        const viewport = viewportRef.current;
        const selectedItem = itemRefs.current[selectedIndex];

        if (!viewport || !selectedItem) {
            return;
        }

        if (focusOnMount) {
            selectedItem.focus();
        }

        const viewportBounds = viewport.getBoundingClientRect();
        const itemBounds = selectedItem.getBoundingClientRect();

        if (itemBounds.top < viewportBounds.top) {
            viewport.scrollTop -= viewportBounds.top - itemBounds.top;
        } else if (itemBounds.bottom > viewportBounds.bottom) {
            viewport.scrollTop += itemBounds.bottom - viewportBounds.bottom;
        }
    },
    [focusOnMount, items, selectedIndex]
);

/**
 * Invokes the supplied command when the indexed item exists; otherwise does nothing.
 */
const selectItem = (index: number) => {
    const item = items[index];

    if (item) {
        command(item);
    }
};

/**
 * Handles close, cyclic navigation and selection keys and reports whether consumed.
 * Postconditions: Escape invokes an available close callback even for empty lists. Empty lists
 * otherwise return false; arrows request a later index update and Enter selects the current index.
 */
const handleKeyDown = (event: Pick<KeyboardEvent, "key">) => {
    if (event.key === "Escape" && onClose) {
        onClose();
        return true;
    }

    if (!items.length) {
        return false;
    }

    if (event.key === "ArrowUp") {
        setSelectedIndex(current => (current + items.length - 1) % items.length);
        return true;
    }

    if (event.key === "ArrowDown") {
        setSelectedIndex(current => (current + 1) % items.length);
        return true;
    }

    if (event.key === "Enter") {
        selectItem(selectedIndex);
        return true;
    }

    return false;
};

useImperativeHandle(
    ref,
    () => ({
        onKeyDown: ({ event }) => handleKeyDown(event)
    })
);

/**
 * Stops default behavior and propagation only for keys handled by the selection logic.
 */
const handleListKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (handleKeyDown(event)) {
        event.preventDefault();
        event.stopPropagation();
    }
};

/**
 * Prevents the default mouse focus action before invoking the indexed field command.
 */
const handleMouseDown = (event: MouseEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    selectItem(index);
};

const content = (
    <div
        className={classes.content}
        onKeyDown={handleListKeyDown}
    >
        <Text
            c="dimmed"
            fw={600}
            px="xs"
            pb={4}
            size="xs"
        >
            Insert merge field
        </Text>
        {items.length ? (
            <ScrollArea.Autosize
                mah={240}
                type="auto"
                viewportRef={viewportRef}
            >
                <div
                    aria-label="Merge fields"
                    className={classes.items}
                    role="listbox"
                >
                    {items.map((item, index) => (
                        <UnstyledButton
                            aria-selected={index === selectedIndex}
                            className={classes.item}
                            data-selected={index === selectedIndex || undefined}
                            data-toolbar-focus={focusOnMount || undefined}
                            key={item.id}
                            onMouseDown={event => handleMouseDown(event, index)}
                            onMouseEnter={() => setSelectedIndex(index)}
                            ref={element => {
                                itemRefs.current[index] = element;
                            }}
                            role="option"
                            tabIndex={index === selectedIndex ? 0 : -1}
                        >
                            <Text
                                fw={500}
                                size="sm"
                            >
                                {item.label}
                            </Text>
                            <Text
                                c="dimmed"
                                size="xs"
                            >
                                {item.description ?? `{{${item.id}}}`}
                            </Text>
                        </UnstyledButton>
                    )
                    )}
                </div>
            </ScrollArea.Autosize>
        ) : (
            <Text
                c="dimmed"
                px="xs"
                py={6}
                size="sm"
            >
                No matching merge fields
            </Text>
        )}
    </div>
);

return withSurface ? (
    <Paper
        className={classes.dropdown}
        p="xs"
        radius="md"
        shadow="md"
        withBorder
    >
        {content}
    </Paper>
) : (
    content
);
}
);

MergeFieldSuggestionList.displayName = "MergeFieldSuggestionList";

/**
 * Builds live merge-field search and suggestion rendering callbacks.
 * Postconditions: Search trims and locale-lowercases the query, matches label/ID/description and
 * returns at most 20 fields in supplied order. The renderer mounts on start, updates props and
 * unmounts before destroying on exit. Escape exits the registered suggestion plugin.
 * Assumptions: The lifecycle supplies mount/unmount and valid editor objects; callback errors propagate.
 */
export const createMergeFieldSuggestion = (
    getMergeFields: () => readonly MergeFieldOption[]
): Partial<MergeFieldOptions["suggestion"]> => ({
    shouldShow: () => getMergeFields().length > 0,
    /**
     * Filters the latest field catalog by the normalized query without reordering matches.
     */
    items: ({ query }) => {
        const normalisedQuery = query.trim().toLocaleLowerCase();

        return getMergeFields()
            .filter(field =>
                !normalisedQuery ||
                field.label.toLocaleLowerCase().includes(normalisedQuery) ||
                field.id.toLocaleLowerCase().includes(normalisedQuery) ||
                field.description?.toLocaleLowerCase().includes(normalisedQuery)
            )
            .slice(0, 20);
    },
    /**
     * Allocates per-suggestion renderer ownership and its mount/update/key/exit callbacks.
     */
    render: () => {
        let component:
            | ReactRenderer<MergeFieldSuggestionListRef, MergeFieldSuggestionListProps>
            | undefined;
        let unmount: (() => void) | undefined;

        return {
            onStart: props => {
                component = new ReactRenderer(MergeFieldSuggestionList, {
                    editor: props.editor,
                    props: {
                        items: props.items,
                        command: props.command,
                        withSurface: true
                    }
                });
                unmount = props.mount(component.element);
            },
            onUpdate: props =>
                component?.updateProps({
                    items: props.items,
                    command: props.command
                }),
            onKeyDown: props => {
                if (props.event.key === "Escape") {
                    exitSuggestion(props.view, mergeFieldSuggestionPluginKey);
                    return true;
                }

                return component?.ref?.onKeyDown(props) ?? false;
            },
            onExit: () => {
                unmount?.();
                component?.destroy();
                unmount = undefined;
                component = undefined;
            }
        };
    }
});
